import { useState, useEffect, useCallback } from 'react';
import type {
  TimesheetWithEntries,
  TimesheetTotals,
  CreateEntryRequest,
  UpdateEntryRequest,
  Timesheet,
  TimesheetListParams,
  EntryPreviewRequest,
  EntryCompliancePreview,
  WeeklyTarget,
} from '@renewal/types';
import {
  getCurrentTimesheet,
  getTimesheetByWeek,
  getTimesheetById,
  getTimesheets,
  createTimesheetEntry,
  createTimesheetEntriesBulk,
  updateTimesheetEntry,
  deleteTimesheetEntry,
  previewEntryCompliance,
  ApiRequestError,
} from '../api/client.js';

interface UseTimesheetOptions {
  weekStartDate?: string; // If not provided, uses current week
}

interface UseTimesheetResult {
  timesheet: TimesheetWithEntries | null;
  totals: TimesheetTotals | null;
  loading: boolean;
  /** Loading the week failed. Never set by a refused save (see below). */
  error: string | null;
  saving: boolean;
  addEntry: (entry: CreateEntryRequest) => Promise<void>;
  addMultipleEntries: (entries: CreateEntryRequest[]) => Promise<void>;
  updateEntry: (entryId: string, updates: UpdateEntryRequest) => Promise<void>;
  updateEntriesSchoolDay: (date: string, isSchoolDay: boolean) => Promise<void>;
  deleteEntry: (entryId: string) => Promise<void>;
  previewEntry: (entry: EntryPreviewRequest) => Promise<EntryCompliancePreview | null>;
  /** Re-read the week, showing the page's loading state. */
  refresh: () => void;
  /** Re-read the week in place, without the loading state. */
  refreshQuietly: () => Promise<void>;
  /** Show the weekly target a submit response carried, until the week is re-read. */
  applyWeeklyTarget: (weeklyTarget: WeeklyTarget) => void;
}

/** "1 of 3 days was not saved. 2026-11-03: <reason>" for a bulk create's per-day failures. */
function bulkFailureMessage(
  entries: CreateEntryRequest[],
  failed: number,
  errors: Array<{ index: number; error: string }>
): string {
  const days = errors
    .map((e) => `${entries[e.index]?.workDate ?? `Day ${e.index + 1}`}: ${e.error}`)
    .join(' ');
  const verb = failed === 1 ? 'was' : 'were';
  return `${failed} of ${entries.length} days ${verb} not saved. ${days}`.trim();
}

/**
 * A route's request check refuses with "Invalid request data" and puts the
 * reasons in `details` (for example a time off the quarter hour). Show the
 * reasons instead of the generic line.
 */
function readableSaveError(err: unknown): unknown {
  if (!(err instanceof ApiRequestError) || err.code !== 'Validation Error') return err;
  const details = err.responseBody['details'];
  if (!Array.isArray(details)) return err;
  const messages = [
    ...new Set(
      details
        .map((d) => (d as { message?: unknown } | null)?.message)
        .filter((m): m is string => typeof m === 'string' && m.length > 0)
    ),
  ];
  if (messages.length === 0) return err;
  return new ApiRequestError(
    messages.join(' '),
    err.status,
    err.code,
    err.endpoint,
    err.responseBody
  );
}

/**
 * Hook for managing a single timesheet with entries.
 *
 * A refused save (overlap, the filler code on a restricted fund, an off-quarter
 * time, a closed fund) is an expected answer, not a broken page: the mutations
 * rethrow it for the popover or edit form to show inline and stay open, and
 * never set the page-level `error`, which replaces the whole screen. After a
 * successful save the week is re-read quietly, so the screen does not flash
 * its loading state.
 */
export function useTimesheet(options: UseTimesheetOptions = {}): UseTimesheetResult {
  const [timesheet, setTimesheet] = useState<TimesheetWithEntries | null>(null);
  const [totals, setTotals] = useState<TimesheetTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchTimesheet = useCallback(
    async (quiet = false) => {
      if (!quiet) setLoading(true);
      setError(null);
      try {
        const result = options.weekStartDate
          ? await getTimesheetByWeek(options.weekStartDate)
          : await getCurrentTimesheet();
        setTimesheet(result);
        setTotals(result.totals);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to load timesheet');
        }
      } finally {
        if (!quiet) setLoading(false);
      }
    },
    [options.weekStartDate]
  );

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  const refresh = useCallback(() => {
    void fetchTimesheet();
  }, [fetchTimesheet]);

  const refreshQuietly = useCallback(() => fetchTimesheet(true), [fetchTimesheet]);

  const applyWeeklyTarget = useCallback((weeklyTarget: WeeklyTarget) => {
    setTotals((prev) =>
      prev
        ? {
            ...prev,
            weeklyTarget,
            weeklyMinutes: weeklyTarget.totalMinutes,
            weekly: weeklyTarget.totalMinutes / 60,
          }
        : prev
    );
  }, []);

  const addEntry = useCallback(
    async (entry: CreateEntryRequest) => {
      if (!timesheet) return;
      setSaving(true);
      try {
        await createTimesheetEntry(timesheet.id, entry);
        await fetchTimesheet(true); // Refresh to get updated totals
      } catch (err) {
        throw readableSaveError(err);
      } finally {
        setSaving(false);
      }
    },
    [timesheet, fetchTimesheet]
  );

  const addMultipleEntries = useCallback(
    async (entries: CreateEntryRequest[]) => {
      if (!timesheet) return;
      setSaving(true);
      try {
        // The server saves every day or none and refuses with one message
        // naming the day. A per-day failure list is still read in case any
        // day comes back unsaved, so a missing day is never silent.
        const result = await createTimesheetEntriesBulk(timesheet.id, entries);
        await fetchTimesheet(true); // Refresh to get updated totals
        if (result.failed > 0) {
          throw new Error(bulkFailureMessage(entries, result.failed, result.errors));
        }
      } catch (err) {
        throw readableSaveError(err);
      } finally {
        setSaving(false);
      }
    },
    [timesheet, fetchTimesheet]
  );

  const updateEntry = useCallback(
    async (entryId: string, updates: UpdateEntryRequest) => {
      if (!timesheet) return;
      setSaving(true);
      try {
        await updateTimesheetEntry(timesheet.id, entryId, updates);
        await fetchTimesheet(true); // Refresh to get updated totals
      } catch (err) {
        throw readableSaveError(err);
      } finally {
        setSaving(false);
      }
    },
    [timesheet, fetchTimesheet]
  );

  const deleteEntry = useCallback(
    async (entryId: string) => {
      if (!timesheet) return;
      setSaving(true);
      try {
        await deleteTimesheetEntry(timesheet.id, entryId);
        await fetchTimesheet(true); // Refresh to get updated totals
      } catch (err) {
        throw readableSaveError(err);
      } finally {
        setSaving(false);
      }
    },
    [timesheet, fetchTimesheet]
  );

  const previewEntry = useCallback(
    async (entry: EntryPreviewRequest): Promise<EntryCompliancePreview | null> => {
      if (!timesheet) return null;
      try {
        return await previewEntryCompliance(timesheet.id, entry);
      } catch (err) {
        if (err instanceof ApiRequestError) {
          console.error('Preview failed:', err.message);
        }
        return null;
      }
    },
    [timesheet]
  );

  // Update isSchoolDay for all entries on a specific date
  const updateEntriesSchoolDay = useCallback(
    async (date: string, isSchoolDay: boolean) => {
      if (!timesheet) return;

      // Find all entries on this date
      const entriesOnDate = timesheet.entries.filter((e) => e.workDate === date);
      if (entriesOnDate.length === 0) return;

      setSaving(true);
      try {
        // Update each entry's isSchoolDay status
        await Promise.all(
          entriesOnDate.map((entry) =>
            updateTimesheetEntry(timesheet.id, entry.id, { isSchoolDay })
          )
        );
        await fetchTimesheet(true); // Refresh to get updated data
      } catch (err) {
        throw readableSaveError(err);
      } finally {
        setSaving(false);
      }
    },
    [timesheet, fetchTimesheet]
  );

  return {
    timesheet,
    totals,
    loading,
    error,
    saving,
    addEntry,
    addMultipleEntries,
    updateEntry,
    updateEntriesSchoolDay,
    deleteEntry,
    previewEntry,
    refresh,
    refreshQuietly,
    applyWeeklyTarget,
  };
}

interface UseTimesheetsOptions {
  status?: 'open' | 'submitted' | 'approved' | 'rejected' | 'all';
  limit?: number;
  offset?: number;
}

interface UseTimesheetsResult {
  timesheets: Timesheet[];
  total: number;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

/**
 * Hook for fetching list of timesheets
 */
export function useTimesheets(options: UseTimesheetsOptions = {}): UseTimesheetsResult {
  const [timesheets, setTimesheets] = useState<Timesheet[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTimesheets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: TimesheetListParams = {};
      if (options.status) params.status = options.status;
      if (options.limit !== undefined) params.limit = options.limit;
      if (options.offset !== undefined) params.offset = options.offset;

      const response = await getTimesheets(params);
      setTimesheets(response.timesheets);
      setTotal(response.total);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load timesheets');
      }
    } finally {
      setLoading(false);
    }
  }, [options.status, options.limit, options.offset]);

  useEffect(() => {
    fetchTimesheets();
  }, [fetchTimesheets]);

  return { timesheets, total, loading, error, refetch: fetchTimesheets };
}

interface UseTimesheetByIdResult {
  timesheet: TimesheetWithEntries | null;
  loading: boolean;
  error: string | null;
  saving: boolean;
  addEntry: (entry: CreateEntryRequest) => Promise<void>;
  updateEntry: (entryId: string, updates: UpdateEntryRequest) => Promise<void>;
  deleteEntry: (entryId: string) => Promise<void>;
  refresh: () => void;
}

/**
 * Hook for fetching a specific timesheet by ID
 */
export function useTimesheetById(id: string | undefined): UseTimesheetByIdResult {
  const [timesheet, setTimesheet] = useState<TimesheetWithEntries | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchTimesheet = useCallback(async () => {
    if (!id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const result = await getTimesheetById(id);
      setTimesheet(result);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load timesheet');
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchTimesheet();
  }, [fetchTimesheet]);

  const addEntry = useCallback(
    async (entry: CreateEntryRequest) => {
      if (!id) return;
      setSaving(true);
      setError(null);
      try {
        await createTimesheetEntry(id, entry);
        await fetchTimesheet();
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to add entry');
        }
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [id, fetchTimesheet]
  );

  const updateEntry = useCallback(
    async (entryId: string, updates: UpdateEntryRequest) => {
      if (!id) return;
      setSaving(true);
      setError(null);
      try {
        await updateTimesheetEntry(id, entryId, updates);
        await fetchTimesheet();
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to update entry');
        }
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [id, fetchTimesheet]
  );

  const deleteEntry = useCallback(
    async (entryId: string) => {
      if (!id) return;
      setSaving(true);
      setError(null);
      try {
        await deleteTimesheetEntry(id, entryId);
        await fetchTimesheet();
      } catch (err) {
        if (err instanceof ApiRequestError) {
          setError(err.message);
        } else {
          setError('Failed to delete entry');
        }
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [id, fetchTimesheet]
  );

  return {
    timesheet,
    loading,
    error,
    saving,
    addEntry,
    updateEntry,
    deleteEntry,
    refresh: fetchTimesheet,
  };
}
